'use client';

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    X, ChevronLeft, ChevronRight, Mail, Phone, FileText, Linkedin,
    CheckCircle2, Clock, AlertCircle, ExternalLink, Mic, RefreshCw,
} from 'lucide-react';
import { applicationApi, type Application } from '../services/application.api';
import { pipelineApi, type Pipeline } from '@/modules/pipeline/services/pipeline.api';
import { useFullPagerStore } from '@/modules/shared/store/useFullPagerStore';
import { ReportRightPanel, type RightTabKey } from '@/modules/reports/components/ReportRightPanel';
import { reportsApi } from '@/modules/reports/services/reports.api';
import type {
    CvScreeningResult,
    AiInterviewResult,
    ReviewerScoreEdit,
    UniversalAggregateResponse,
    AggregatedCriterion,
    AggregatedRequirement,
} from '@/modules/reports/types';
import { tasksApi } from '@/modules/tasks/services/tasks.api';
import type { TaskItem } from '@/modules/shared/types/yc-demo';
import { idibuApi } from '@/modules/idibu';
import { useAuth } from '@/auth/AuthProvider';
import ApplicationActionBar from './ApplicationActionBar';
import ResumeViewer from './ResumeViewer';
import { showErrorToast, showSuccessToast } from '@/services/toastService';

/* ───────── Tokens ───────── */

const FONT_SANS = 'var(--font-sans)';
const FONT_SERIF = 'var(--font-serif)';
const TEXT_PRIMARY = 'var(--text-primary)';
const TEXT_SECONDARY = 'var(--text-secondary)';
const TEXT_TERTIARY = 'var(--text-tertiary)';
const TEXT_QUATERNARY = 'var(--text-quaternary)';
const ACCENT = 'var(--accent)';
const ACCENT_SUBTLE = 'var(--accent-subtle)';
const SURFACE_PRIMARY = 'var(--surface-primary)';
const SURFACE_SECONDARY = 'var(--surface-secondary)';
const SURFACE_TERTIARY = 'var(--surface-tertiary)';
const SURFACE_CANVAS = 'var(--surface-canvas)';
const BORDER_DEFAULT = 'var(--border-default)';
const BORDER_MUTED = 'var(--border-muted)';
const FG_SUCCESS = 'var(--fg-success)';
const BG_SUCCESS = 'var(--bg-success)';
const FG_DANGER = 'var(--fg-danger)';
const BG_DANGER = 'var(--bg-danger)';
const FG_WARNING = 'var(--fg-warning)';
const BG_WARNING = 'var(--bg-warning)';

/* ───────── Helpers ───────── */

function getInitials(name: string): string {
    return name.split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
}

// DiceBear/UI-Avatars/etc. are cartoon placeholders, not real photos —
// prefer the clean initials avatar in those cases.
function isPlaceholderAvatar(url?: string | null): boolean {
    if (!url) return true;
    return /dicebear|ui-avatars|boringavatars/i.test(url);
}

function formatScore(n: number): string {
    return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

const VERDICT_META = {
    PASS:    { label: 'Strong match', bg: BG_SUCCESS, fg: FG_SUCCESS },
    REVIEW:  { label: 'Needs review', bg: BG_WARNING, fg: FG_WARNING },
    FAIL:    { label: 'Not a fit',    bg: BG_DANGER,  fg: FG_DANGER },
    PENDING: { label: 'Pending',      bg: SURFACE_TERTIARY, fg: TEXT_TERTIARY },
} as const;

function sectionToRightTab(section: string | null, hasCv: boolean, hasAi: boolean): RightTabKey {
    const s = (section || '').toLowerCase();
    if (s.startsWith('cv_screening') || s === 'screening' || s.startsWith('screening:')) return 'cv_screening';
    if (s.startsWith('ai_interview') || s === 'interview' || s.startsWith('interview:')) return 'ai_interview';
    if (s === 'panel') return 'panel';
    if (s === 'transcript') return 'transcript';
    if (s === 'activity' || s === 'profile' || s === 'applied') return 'activity';
    if (s === 'notes') return 'notes';
    return hasCv ? 'cv_screening' : hasAi ? 'ai_interview' : 'activity';
}

function rightTabToSection(tab: RightTabKey): string {
    return tab;
}

/* ───────── Main ───────── */

interface Props { applicationId: string; }

export default function ApplicationFullPager({ applicationId }: Props) {
    const { activeSection, setSection, setBreadcrumbs, taskId, scope, close, breadcrumbs, navigationContext, navigateTo, isNavigating } = useFullPagerStore();
    const { user } = useAuth();

    const [application, setApplication] = useState<Application | null>(null);
    const [pipeline, setPipeline] = useState<Pipeline | null>(null);
    const [cvScreening, setCvScreening] = useState<CvScreeningResult | null>(null);
    const [aiInterview, setAiInterview] = useState<AiInterviewResult | null>(null);
    const [universal, setUniversal] = useState<UniversalAggregateResponse | null>(null);
    const [task, setTask] = useState<TaskItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [markingTask, setMarkingTask] = useState(false);

    const loadData = useCallback(async (appId: string, tId: string | null) => {
        setLoading(true);
        setError(null);
        try {
            const [{ data: app }, cv, ai, uni, taskDoc] = await Promise.all([
                applicationApi.getApplication(appId),
                reportsApi.getCvScreening(appId).catch(() => null),
                reportsApi.getAIInterview(appId).catch(() => null),
                reportsApi.getUniversalAggregate(appId).catch(() => null),
                tId ? tasksApi.get(tId).catch(() => null) : Promise.resolve(null),
            ]);
            setApplication(app);
            setCvScreening(cv);
            setAiInterview(ai);
            setUniversal(uni);
            setTask(taskDoc);

            if (app.pipelineId) {
                try {
                    const { data: pipe } = await pipelineApi.getPipeline(app.pipelineId);
                    setPipeline(pipe);
                } catch {
                    setPipeline(null);
                }
            }

            setBreadcrumbs([
                { label: 'Jobs', href: '/v2/jobs' },
                { label: app.jobTitle || 'Job', href: `/v2/jobs/${app.jobPostingId}` },
                { label: app.candidate.name },
            ]);
        } catch (err: any) {
            setError(err.response?.data?.error || err.message || 'Failed to load application');
        } finally {
            setLoading(false);
        }
    }, [setBreadcrumbs]);

    useEffect(() => {
        loadData(applicationId, taskId || null);
    }, [applicationId, taskId, loadData]);

    const handleRefresh = useCallback(() => {
        loadData(applicationId, taskId || null);
    }, [applicationId, taskId, loadData]);

    const handleMarkTaskComplete = useCallback(async () => {
        if (!task) return;
        setMarkingTask(true);
        try {
            await tasksApi.setStatus(task._id, 'completed');
            showSuccessToast('Task marked complete.');
            const refreshed = await tasksApi.get(task._id).catch(() => null);
            setTask(refreshed);
        } catch (err) {
            console.error('Failed to mark task complete', err);
            showErrorToast("Couldn't complete the task. Try again.");
        } finally {
            setMarkingTask(false);
        }
    }, [task]);

    // Edit function — when reviewer overrides a score, update local state and
    // refetch universal aggregate so the LEFT framework reflects the change.
    const handleEdited = useCallback((edit: ReviewerScoreEdit) => {
        if (!application) return;
        if (edit.reportKind === 'cv_screening' && cvScreening) {
            setCvScreening({
                ...cvScreening,
                reviewerFeedback: [...cvScreening.reviewerFeedback, edit],
            });
        } else if (edit.reportKind === 'ai_interview' && aiInterview) {
            setAiInterview({
                ...aiInterview,
                reviewerFeedback: [...aiInterview.reviewerFeedback, edit],
            });
        }
        reportsApi.getUniversalAggregate(application._id).then(setUniversal).catch(() => {});
    }, [application, cvScreening, aiInterview]);

    const activeRightTab = useMemo(
        () => sectionToRightTab(activeSection, !!cvScreening, !!aiInterview),
        [activeSection, cvScreening, aiInterview],
    );

    if (loading) return <LoadingShell />;

    if (error || !application) {
        return (
            <div
                className="flex items-center justify-center h-full"
                style={{ background: SURFACE_CANVAS, fontFamily: FONT_SANS }}
            >
                <div style={{ textAlign: 'center' }}>
                    <p style={{ color: FG_DANGER, marginBottom: 8, fontSize: 13 }}>{error || 'Application not found'}</p>
                    <button
                        onClick={() => loadData(applicationId, taskId || null)}
                        style={{ fontSize: 12, color: ACCENT, textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none' }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    const userName = user?.firstName ? `${user.firstName} ${user.lastName ?? ''}`.trim() : (user?.email || 'You');

    return (
        <div
            style={{
                display: 'flex', flexDirection: 'column', height: '100%',
                background: SURFACE_CANVAS, fontFamily: FONT_SANS,
            }}
        >
            <TopBar
                breadcrumbs={breadcrumbs}
                onClose={close}
                navigationContext={navigationContext}
                navigateTo={navigateTo}
                isNavigating={isNavigating}
            />

            <div
                style={{
                    flex: 1, display: 'flex', gap: 18,
                    padding: '14px 22px', minHeight: 0, overflow: 'hidden',
                }}
            >
                <LeftPanel
                    application={application}
                    pipeline={pipeline}
                    universal={universal}
                    cvScreening={cvScreening}
                    aiInterview={aiInterview}
                />
                <RightPanel
                    application={application}
                    activeTab={activeRightTab}
                    onTabChange={(tab) => setSection(rightTabToSection(tab))}
                    cvScreening={cvScreening}
                    aiInterview={aiInterview}
                    onEdited={handleEdited}
                    userName={userName}
                    task={task}
                    scope={scope}
                />
            </div>

            <ActionFooter
                application={application}
                pipelineStages={pipeline?.stages || []}
                task={task}
                markingTask={markingTask}
                onMarkComplete={handleMarkTaskComplete}
                onRefresh={handleRefresh}
            />
        </div>
    );
}

/* ───────── Top Bar ───────── */

interface TopBarProps {
    breadcrumbs: Array<{ label: string; href?: string }>;
    onClose: () => void;
    navigationContext: { currentIndex: number; total: number } | null;
    navigateTo: (i: number) => void;
    isNavigating: boolean;
}

function TopBar({ breadcrumbs, onClose, navigationContext, navigateTo, isNavigating }: TopBarProps) {
    const desktop = typeof window !== 'undefined' ? window.lanternDesktop : undefined;
    const isDarwinDesktop = desktop?.isDesktop && desktop.platform === 'darwin';
    const headerStyle: React.CSSProperties = isDarwinDesktop
        ? { paddingLeft: 88, WebkitAppRegion: 'drag' as any }
        : { paddingLeft: 22 };
    const noDragStyle: React.CSSProperties = isDarwinDesktop
        ? { WebkitAppRegion: 'no-drag' as any }
        : {};

    const hasPrev = navigationContext && navigationContext.currentIndex > 0;
    const hasNext = navigationContext && navigationContext.currentIndex < navigationContext.total - 1;

    return (
        <header
            style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                height: isDarwinDesktop ? 56 : 44,
                paddingRight: 14,
                borderBottom: `1px solid ${BORDER_MUTED}`,
                background: SURFACE_PRIMARY,
                flexShrink: 0,
                ...headerStyle,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, minWidth: 0, color: TEXT_TERTIARY }}>
                {breadcrumbs.map((crumb, i) => (
                    <React.Fragment key={i}>
                        {i > 0 && (
                            <span style={{ color: TEXT_QUATERNARY, fontSize: 10 }}>›</span>
                        )}
                        {crumb.href ? (
                            <button
                                onClick={() => { onClose(); if (typeof window !== 'undefined') window.location.href = crumb.href!; }}
                                style={{
                                    background: 'none', border: 'none', cursor: 'pointer',
                                    color: TEXT_TERTIARY, padding: 0,
                                    maxWidth: 220, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                                    fontFamily: 'inherit', fontSize: 'inherit',
                                    ...noDragStyle,
                                }}
                            >
                                {crumb.label}
                            </button>
                        ) : (
                            <span style={{
                                color: TEXT_PRIMARY, fontWeight: 500,
                                maxWidth: 280, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap',
                            }}>
                                {crumb.label}
                            </span>
                        )}
                    </React.Fragment>
                ))}
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 8, ...noDragStyle }}>
                {navigationContext && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <button
                                onClick={() => hasPrev && navigateTo(navigationContext.currentIndex - 1)}
                                disabled={!hasPrev || isNavigating}
                                style={navIconButton(!hasPrev || isNavigating)}
                                aria-label="Previous"
                            >
                                <ChevronLeft size={14} strokeWidth={1.75} style={{ color: TEXT_SECONDARY }} />
                            </button>
                            <button
                                onClick={() => hasNext && navigateTo(navigationContext.currentIndex + 1)}
                                disabled={!hasNext || isNavigating}
                                style={navIconButton(!hasNext || isNavigating)}
                                aria-label="Next"
                            >
                                <ChevronRight size={14} strokeWidth={1.75} style={{ color: TEXT_SECONDARY }} />
                            </button>
                        </div>
                        <span style={{ fontSize: 11, color: TEXT_TERTIARY, fontVariantNumeric: 'tabular-nums' }}>
                            {isNavigating ? '…' : `${navigationContext.currentIndex + 1} / ${navigationContext.total}`}
                        </span>
                        <span style={{ width: 1, height: 14, background: BORDER_MUTED, marginLeft: 4 }} />
                    </>
                )}
                <button
                    onClick={onClose}
                    style={navIconButton(false)}
                    aria-label="Close"
                >
                    <X size={15} strokeWidth={1.75} style={{ color: TEXT_SECONDARY }} />
                </button>
            </div>
        </header>
    );
}

function navIconButton(disabled: boolean): React.CSSProperties {
    return {
        width: 26, height: 26, borderRadius: 6,
        background: 'transparent',
        border: '1px solid transparent',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.35 : 1,
        transition: 'background 120ms',
    };
}

/* ───────── Loading ───────── */

function LoadingShell() {
    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: SURFACE_CANVAS, fontFamily: FONT_SANS }}>
            <div style={{ height: 44, borderBottom: `1px solid ${BORDER_MUTED}`, background: SURFACE_PRIMARY }} />
            <div style={{ flex: 1, display: 'flex', gap: 18, padding: '14px 22px', minHeight: 0, position: 'relative' }}>
                <div
                    style={{
                        flex: '1 1 0', minWidth: 0, background: SURFACE_PRIMARY,
                        border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 14,
                        padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14,
                    }}
                >
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                        <div className="animate-pulse" style={{ width: 56, height: 56, borderRadius: '50%', background: SURFACE_TERTIARY }} />
                        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div className="animate-pulse" style={{ height: 18, width: '45%', background: SURFACE_TERTIARY, borderRadius: 6 }} />
                            <div className="animate-pulse" style={{ height: 12, width: '70%', background: SURFACE_TERTIARY, borderRadius: 6 }} />
                        </div>
                    </div>
                    <div className="animate-pulse" style={{ height: 1, background: BORDER_MUTED }} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div className="animate-pulse" style={{ height: 12, width: 120, background: SURFACE_TERTIARY, borderRadius: 4 }} />
                        <div className="animate-pulse" style={{ height: 14, width: '90%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                        <div className="animate-pulse" style={{ height: 14, width: '85%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                        <div className="animate-pulse" style={{ height: 14, width: '75%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                    </div>
                </div>
                <div
                    style={{
                        flex: 1, minWidth: 0, background: SURFACE_PRIMARY,
                        border: `1px solid ${BORDER_DEFAULT}`, borderRadius: 14,
                        padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14,
                    }}
                >
                    <div style={{ display: 'flex', gap: 8 }}>
                        <div className="animate-pulse" style={{ height: 26, width: 90, background: SURFACE_TERTIARY, borderRadius: 8 }} />
                        <div className="animate-pulse" style={{ height: 26, width: 90, background: SURFACE_TERTIARY, borderRadius: 8 }} />
                        <div className="animate-pulse" style={{ height: 26, width: 70, background: SURFACE_TERTIARY, borderRadius: 8 }} />
                    </div>
                    <div className="animate-pulse" style={{ height: 14, width: '80%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                    <div className="animate-pulse" style={{ height: 14, width: '92%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                    <div className="animate-pulse" style={{ height: 14, width: '70%', background: SURFACE_TERTIARY, borderRadius: 4 }} />
                </div>
                <div
                    style={{
                        position: 'absolute', inset: 0,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 10,
                            padding: '10px 16px', borderRadius: 9999,
                            background: SURFACE_PRIMARY,
                            border: `1px solid ${BORDER_DEFAULT}`,
                            boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                            fontSize: 12.5, color: TEXT_TERTIARY, fontWeight: 500,
                        }}
                    >
                        <span
                            style={{
                                width: 14, height: 14, borderRadius: '50%',
                                border: `2px solid ${BORDER_DEFAULT}`,
                                borderTopColor: ACCENT,
                            }}
                            className="animate-spin"
                        />
                        Loading report…
                    </div>
                </div>
            </div>
            <div style={{ height: 56, borderTop: `1px solid ${BORDER_MUTED}`, background: SURFACE_PRIMARY }} />
        </div>
    );
}

/* ───────── LEFT — summary card ───────── */

type Justifications = Record<string, { cv?: string; ai?: string }>;

function buildJustifications(
    cvScreening: CvScreeningResult | null,
    aiInterview: AiInterviewResult | null,
): Justifications {
    const map: Justifications = {};
    const put = (id: string, source: 'cv' | 'ai', text?: string) => {
        if (!text) return;
        if (!map[id]) map[id] = {};
        map[id][source] = text;
    };
    cvScreening?.criteriaResult?.criteria.forEach((c) => put(c.criterionId, 'cv', c.justification));
    cvScreening?.profileStrength?.components.forEach((c) => put(c.componentId, 'cv', c.justification));
    aiInterview?.perRequirement?.forEach((r) => put(r.requirementId, 'ai', r.justification));
    aiInterview?.perCriterion?.forEach((c) => put(c.criterionId, 'ai', c.justification));
    return map;
}

function LeftPanel({
    application, pipeline, universal, cvScreening, aiInterview,
}: {
    application: Application;
    pipeline: Pipeline | null;
    universal: UniversalAggregateResponse | null;
    cvScreening: CvScreeningResult | null;
    aiInterview: AiInterviewResult | null;
}) {
    const justifications = useMemo(
        () => buildJustifications(cvScreening, aiInterview),
        [cvScreening, aiInterview],
    );

    return (
        <div
            style={{
                flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column',
                background: SURFACE_PRIMARY,
                border: `1px solid ${BORDER_DEFAULT}`,
                borderRadius: 14,
                overflow: 'hidden',
            }}
        >
            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <IdentityHero application={application} universal={universal} />
                <QuickActions application={application} />
                {pipeline && pipeline.stages.length > 0 && (
                    <PipelineStrip
                        stages={pipeline.stages}
                        currentStageId={application.currentStageId}
                        overallStatus={application.overallStatus}
                    />
                )}
                {application.source === 'sourcing' && application.sourcedProfile ? (
                    <SourcedProfilePanel name={application.candidate.name} profile={application.sourcedProfile} />
                ) : (
                    <FrameworkSection universal={universal} justifications={justifications} />
                )}
                {application.source === 'idibu'
                    && (application.overallStatus === 'hired' || application.overallStatus === 'rejected') && (
                        <IdibuSyncFooter application={application} />
                    )}
            </div>
        </div>
    );
}

/* ───────── Identity Hero ───────── */

function IdentityHero({
    application, universal,
}: {
    application: Application;
    universal: UniversalAggregateResponse | null;
}) {
    const initials = getInitials(application.candidate.name);
    const score = universal?.aggregate.score ?? null;
    const verdict = universal?.aggregate.verdict ?? 'PENDING';
    const verdictMeta = VERDICT_META[verdict];

    const status = application.overallStatus;
    const statusMap: Record<string, { label: string; bg: string; fg: string }> = {
        active:   { label: 'Active',   bg: ACCENT_SUBTLE, fg: ACCENT },
        hired:    { label: 'Hired',    bg: BG_SUCCESS, fg: FG_SUCCESS },
        rejected: { label: 'Rejected', bg: BG_DANGER,  fg: FG_DANGER },
    };
    const statusS = statusMap[status] || { label: status, bg: SURFACE_TERTIARY, fg: TEXT_TERTIARY };

    const scoreColor = score == null ? TEXT_QUATERNARY :
        verdict === 'PASS'   ? FG_SUCCESS :
        verdict === 'REVIEW' ? FG_WARNING :
        verdict === 'FAIL'   ? FG_DANGER : TEXT_TERTIARY;

    return (
        <div style={{
            display: 'flex', gap: 16, alignItems: 'center',
            padding: '18px 22px 14px',
        }}>
            {/* Avatar */}
            <div
                style={{
                    width: 56, height: 56, borderRadius: '50%',
                    background: SURFACE_TERTIARY, color: TEXT_SECONDARY,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: 500, fontSize: 19, flexShrink: 0,
                    fontFamily: FONT_SERIF, letterSpacing: 0.5,
                    overflow: 'hidden',
                }}
            >
                {application.candidate.avatarUrl && !isPlaceholderAvatar(application.candidate.avatarUrl) ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img src={application.candidate.avatarUrl} alt={application.candidate.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : initials}
            </div>

            {/* Name + meta */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 20, fontWeight: 600, color: TEXT_PRIMARY, lineHeight: 1.2 }}>
                        {application.candidate.name}
                    </span>
                    <Pill bg={statusS.bg} fg={statusS.fg}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: statusS.fg }} />
                        {statusS.label}
                    </Pill>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6, fontSize: 13, color: TEXT_TERTIARY, flexWrap: 'wrap' }}>
                    <span>Applied {new Date(application.appliedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    {application.candidate.email && (
                        <>
                            <span style={{ color: TEXT_QUATERNARY }}>·</span>
                            <a
                                href={`mailto:${application.candidate.email}`}
                                style={{ color: TEXT_TERTIARY, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 5 }}
                                title={application.candidate.email}
                            >
                                <Mail size={12} strokeWidth={1.75} />
                                <span style={{ maxWidth: 240, textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                                    {application.candidate.email}
                                </span>
                            </a>
                        </>
                    )}
                    {application.candidate.phone && (
                        <>
                            <span style={{ color: TEXT_QUATERNARY }}>·</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                                <Phone size={12} strokeWidth={1.75} />
                                {application.candidate.phone}
                            </span>
                        </>
                    )}
                    <span style={{ color: TEXT_QUATERNARY }}>·</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                        Source <span style={{ color: TEXT_SECONDARY, fontWeight: 500 }}>{application.source || 'Direct'}</span>
                        {application.source === 'idibu' && application.sourceRef?.idibuPortalName && (
                            <span style={{ color: TEXT_QUATERNARY }}>· {application.sourceRef.idibuPortalName}</span>
                        )}
                    </span>
                </div>
            </div>

            {/* Score */}
            {score != null ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6, flexShrink: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 3, lineHeight: 1 }}>
                        <span style={{ fontFamily: FONT_SERIF, fontWeight: 500, fontSize: 44, color: scoreColor }}>
                            {score}
                        </span>
                        <span style={{ fontFamily: FONT_SERIF, color: TEXT_TERTIARY, fontSize: 15 }}>/ 100</span>
                    </div>
                    <Pill bg={verdictMeta.bg} fg={verdictMeta.fg}>
                        <span style={{ width: 5, height: 5, borderRadius: '50%', background: verdictMeta.fg }} />
                        {verdictMeta.label}
                    </Pill>
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4, flexShrink: 0 }}>
                    <span style={{ fontFamily: FONT_SERIF, fontWeight: 500, fontSize: 32, color: TEXT_QUATERNARY }}>—</span>
                    <span style={{ fontSize: 10.5, color: TEXT_QUATERNARY, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Not yet evaluated</span>
                </div>
            )}
        </div>
    );
}

function Pill({ bg, fg, children, size = 'sm' }: { bg: string; fg: string; children: React.ReactNode; size?: 'sm' | 'xs' }) {
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: size === 'xs' ? '2px 8px' : '3px 10px',
                borderRadius: 9999,
                fontSize: size === 'xs' ? 10.5 : 11.5,
                fontWeight: 600,
                background: bg, color: fg,
                lineHeight: 1.4,
                whiteSpace: 'nowrap',
            }}
        >
            {children}
        </span>
    );
}

/* ───────── Quick Actions ───────── */

function QuickActions({ application }: { application: Application }) {
    const cv = application.documents.find((d) => d.type === 'resume');
    const linkedinFromCv = (application.customFields?.questionAnswers as any)?.linkedin
        || (application.customFields?.linkedinUrl as string | undefined);

    const [resumeOpen, setResumeOpen] = useState(false);

    type Chip =
        | { kind: 'link'; label: string; icon: typeof FileText; href: string }
        | { kind: 'action'; label: string; icon: typeof FileText; onClick: () => void };

    const chips: Chip[] = [];
    if (cv) chips.push({ kind: 'action', label: 'View CV', icon: FileText, onClick: () => setResumeOpen(true) });
    if (typeof linkedinFromCv === 'string' && linkedinFromCv) {
        chips.push({ kind: 'link', label: 'LinkedIn', icon: Linkedin, href: linkedinFromCv });
    }
    const others = application.documents.filter((d) => d.type !== 'resume');
    others.slice(0, 1).forEach((doc) => {
        chips.push({
            kind: 'link',
            label: doc.filename.length > 18 ? doc.filename.slice(0, 18) + '…' : doc.filename,
            icon: FileText,
            href: doc.url,
        });
    });

    if (chips.length === 0) return null;

    const chipStyle: React.CSSProperties = {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '6px 13px', borderRadius: 9999,
        fontSize: 12.5, fontWeight: 500,
        color: TEXT_SECONDARY,
        background: SURFACE_SECONDARY,
        border: `1px solid ${BORDER_MUTED}`,
        textDecoration: 'none',
        fontFamily: 'inherit',
        cursor: 'pointer',
        transition: 'background 120ms',
    };

    return (
        <>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', padding: '0 22px 12px' }}>
                {chips.map((c) => {
                    const Icon = c.icon;
                    if (c.kind === 'link') {
                        return (
                            <a
                                key={c.label}
                                href={c.href}
                                target="_blank"
                                rel="noreferrer"
                                style={chipStyle}
                            >
                                <Icon size={13} strokeWidth={1.75} style={{ color: TEXT_TERTIARY }} />
                                {c.label}
                                <ExternalLink size={10} strokeWidth={1.75} style={{ color: TEXT_QUATERNARY }} />
                            </a>
                        );
                    }
                    return (
                        <button
                            key={c.label}
                            type="button"
                            onClick={c.onClick}
                            style={chipStyle}
                        >
                            <Icon size={13} strokeWidth={1.75} style={{ color: TEXT_TERTIARY }} />
                            {c.label}
                        </button>
                    );
                })}
            </div>
            <ResumeViewer
                open={resumeOpen}
                onClose={() => setResumeOpen(false)}
                url={cv?.url ?? null}
                filename={cv?.filename}
            />
        </>
    );
}

/* ───────── Pipeline strip — minimal stepper ───────── */

function PipelineStrip({
    stages, currentStageId, overallStatus,
}: {
    stages: Pipeline['stages'];
    currentStageId: string;
    overallStatus: string;
}) {
    const sorted = [...stages].sort((a, b) => a.order - b.order);
    const currentIdx = sorted.findIndex((s) => s._id === currentStageId);
    const isTerminal = overallStatus === 'rejected' || overallStatus === 'hired';

    return (
        <div style={{
            padding: '12px 22px',
            borderTop: `1px solid ${BORDER_MUTED}`,
            borderBottom: `1px solid ${BORDER_MUTED}`,
            background: SURFACE_SECONDARY,
        }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                {sorted.map((stage, i) => {
                    const isPast = i < currentIdx;
                    const isCurrent = i === currentIdx && !isTerminal;
                    const isHired = i === currentIdx && overallStatus === 'hired';
                    const isRejectedHere = i === currentIdx && overallStatus === 'rejected';

                    const dotColor = isPast || isHired ? FG_SUCCESS :
                        isCurrent ? ACCENT :
                        isRejectedHere ? FG_DANGER :
                        BORDER_DEFAULT;
                    const dotFill = isPast || isHired ? FG_SUCCESS :
                        isCurrent ? ACCENT :
                        isRejectedHere ? FG_DANGER :
                        'transparent';
                    const labelColor = isCurrent ? TEXT_PRIMARY :
                        isPast || isHired ? TEXT_SECONDARY :
                        TEXT_QUATERNARY;
                    const labelWeight = isCurrent ? 600 : 500;

                    return (
                        <React.Fragment key={stage._id}>
                            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, flexShrink: 0, minWidth: 0 }}>
                                <div
                                    style={{
                                        width: 12, height: 12, borderRadius: '50%',
                                        border: `1.5px solid ${dotColor}`,
                                        background: dotFill,
                                        boxShadow: isCurrent ? `0 0 0 3px ${ACCENT_SUBTLE}` : 'none',
                                    }}
                                />
                                <span style={{
                                    fontSize: 11, fontWeight: labelWeight,
                                    color: labelColor, lineHeight: 1.25,
                                    whiteSpace: 'nowrap', maxWidth: 96, overflow: 'hidden', textOverflow: 'ellipsis',
                                }} title={stage.name}>
                                    {stage.name}
                                </span>
                            </div>
                            {i < sorted.length - 1 && (
                                <div style={{
                                    height: 1.5, flex: 1, minWidth: 14, marginBottom: 16,
                                    background: i < currentIdx ? FG_SUCCESS : BORDER_DEFAULT,
                                }} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        </div>
    );
}

/* ───────── Universal framework section ───────── */

function FrameworkSection({
    universal, justifications,
}: {
    universal: UniversalAggregateResponse | null;
    justifications: Justifications;
}) {
    if (!universal) {
        return (
            <div style={{ padding: '22px', borderTop: `1px solid ${BORDER_MUTED}` }}>
                <SectionLabel>Evaluation</SectionLabel>
                <div style={{
                    marginTop: 8,
                    padding: '14px 16px',
                    background: SURFACE_SECONDARY,
                    border: `1px dashed ${BORDER_DEFAULT}`,
                    borderRadius: 10,
                    fontSize: 12.5, color: TEXT_TERTIARY, lineHeight: 1.5,
                }}>
                    No evaluation framework configured for this role.
                </div>
            </div>
        );
    }

    const { aggregate } = universal;
    const allReqsMet = aggregate.requirementsTotal === 0 || aggregate.requirementsMet === aggregate.requirementsTotal;

    return (
        <div style={{ padding: '14px 22px 14px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <SectionLabel>Universal evaluation</SectionLabel>
                <Sources cv={universal.sources.cv.present} ai={universal.sources.ai.present} />
            </div>

            {aggregate.requirements.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                    <SubsectionHeader
                        title="Hard requirements"
                        right={
                            <span style={{ color: allReqsMet ? FG_SUCCESS : FG_DANGER, fontVariantNumeric: 'tabular-nums' }}>
                                {aggregate.requirementsMet} / {aggregate.requirementsTotal} met
                            </span>
                        }
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {aggregate.requirements.map((r, i) => (
                            <RequirementRow
                                key={r.requirementId}
                                req={r}
                                justification={justifications[r.requirementId]}
                                isLast={i === aggregate.requirements.length - 1}
                            />
                        ))}
                    </div>
                </div>
            )}

            {aggregate.criteria.length > 0 && (
                <div>
                    <SubsectionHeader
                        title="Scored criteria"
                        right={
                            <span style={{ color: TEXT_QUATERNARY }}>
                                {aggregate.evaluatedCount} / {aggregate.criteria.length} evaluated
                            </span>
                        }
                    />
                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                        {aggregate.criteria.map((c, i) => (
                            <CriterionRow
                                key={c.criterionId}
                                crit={c}
                                totalWeight={aggregate.totalWeight || 0}
                                justification={justifications[c.criterionId]}
                                isLast={i === aggregate.criteria.length - 1}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ───────── Sourced profile panel (replaces FrameworkSection for source=sourcing) ───────── */

function SourcedProfilePanel({
    name,
    profile,
}: {
    name: string;
    profile: NonNullable<Application['sourcedProfile']>;
}) {
    const skills = profile.skills || [];
    const experience = profile.experience || [];
    const education = profile.education || [];
    const meta: Array<{ icon: React.ReactNode; text: string }> = [];
    if (profile.location) meta.push({ icon: <span style={{ fontSize: 11 }}>📍</span>, text: profile.location });
    if (profile.yearsExperience != null && profile.yearsExperience > 0) {
        meta.push({ icon: <span style={{ fontSize: 11 }}>💼</span>, text: `${profile.yearsExperience}y experience` });
    }
    if (profile.matchScore != null) {
        meta.push({ icon: <span style={{ fontSize: 11 }}>⚡</span>, text: `Match ${profile.matchScore}` });
    }

    return (
        <div style={{ padding: '14px 22px 22px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
                <SectionLabel>Sourced profile</SectionLabel>
                {profile.linkedinUrl && (
                    <a
                        href={profile.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '2px 8px', borderRadius: 999,
                            fontSize: 11, fontWeight: 500,
                            color: TEXT_SECONDARY, textDecoration: 'none',
                            border: `1px solid ${BORDER_DEFAULT}`,
                            background: SURFACE_PRIMARY,
                        }}
                    >
                        <Linkedin size={11} strokeWidth={2} />
                        LinkedIn
                        <ExternalLink size={9} strokeWidth={2} />
                    </a>
                )}
            </div>

            {profile.headline && (
                <p style={{
                    fontSize: 13.5, color: TEXT_PRIMARY, lineHeight: 1.45,
                    marginBottom: 10,
                }}>
                    {profile.headline}
                </p>
            )}
            {meta.length > 0 && (
                <div style={{
                    display: 'flex', flexWrap: 'wrap', gap: '4px 12px',
                    fontSize: 11.5, color: TEXT_TERTIARY,
                    marginBottom: 16,
                }}>
                    {meta.map((m, i) => (
                        <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            {m.icon}{m.text}
                        </span>
                    ))}
                </div>
            )}

            {profile.summary && (
                <div style={{ marginBottom: 18 }}>
                    <SubsectionHeader title="Summary" />
                    <p style={{
                        fontSize: 12.5, color: TEXT_SECONDARY, lineHeight: 1.55,
                        marginTop: 8, marginBottom: 0,
                    }}>
                        {profile.summary}
                    </p>
                </div>
            )}

            {experience.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                    <SubsectionHeader title="Experience" />
                    <ol style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 12 }}>
                        {experience.map((role, i) => (
                            <li key={`${role.company}-${i}`} style={{ display: 'flex', gap: 10 }}>
                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, paddingTop: 4 }}>
                                    <span style={{
                                        width: 7, height: 7, borderRadius: '50%',
                                        background: role.isCurrent ? ACCENT : BORDER_DEFAULT,
                                    }} />
                                    {i < experience.length - 1 && (
                                        <span style={{ width: 1, flex: 1, background: BORDER_MUTED, marginTop: 4 }} />
                                    )}
                                </div>
                                <div style={{ minWidth: 0, paddingBottom: 4 }}>
                                    <div style={{ fontSize: 12.5, color: TEXT_PRIMARY, lineHeight: 1.4 }}>
                                        <span style={{ fontWeight: 600 }}>{role.title || 'Role'}</span>
                                        {role.company && (
                                            <span style={{ color: TEXT_TERTIARY }}> · {role.company}</span>
                                        )}
                                    </div>
                                    {(role.startDate || role.endDate || role.isCurrent) && (
                                        <div style={{ fontSize: 11, color: TEXT_QUATERNARY, marginTop: 2 }}>
                                            {role.startDate || ''}{(role.startDate && (role.endDate || role.isCurrent)) ? ' — ' : ''}{role.isCurrent ? 'Present' : (role.endDate || '')}
                                        </div>
                                    )}
                                    {role.description && (
                                        <p style={{ fontSize: 12, color: TEXT_SECONDARY, marginTop: 4, marginBottom: 0, lineHeight: 1.45 }}>
                                            {role.description}
                                        </p>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ol>
                </div>
            )}

            {education.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                    <SubsectionHeader title="Education" />
                    <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {education.map((edu, i) => (
                            <li key={`${edu.school}-${i}`} style={{ fontSize: 12.5, color: TEXT_SECONDARY, lineHeight: 1.45 }}>
                                <span style={{ fontWeight: 600, color: TEXT_PRIMARY }}>{edu.school}</span>
                                {(edu.degree || edu.field) && (
                                    <span style={{ color: TEXT_TERTIARY }}>
                                        {' '}— {[edu.degree, edu.field].filter(Boolean).join(', ')}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {skills.length > 0 && (
                <div>
                    <SubsectionHeader title="Skills" />
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                        {skills.map((s) => (
                            <span
                                key={s}
                                style={{
                                    fontSize: 11, padding: '3px 9px', borderRadius: 999,
                                    background: SURFACE_SECONDARY,
                                    border: `1px solid ${BORDER_MUTED}`,
                                    color: TEXT_SECONDARY,
                                }}
                            >
                                {s}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <span style={{
            fontSize: 10.5, fontWeight: 600,
            color: TEXT_TERTIARY,
            textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>
            {children}
        </span>
    );
}

function SubsectionHeader({ title, right }: { title: string; right?: React.ReactNode }) {
    return (
        <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            paddingBottom: 7, marginBottom: 2,
            borderBottom: `1px solid ${BORDER_MUTED}`,
        }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: TEXT_PRIMARY }}>
                {title}
            </span>
            {right && <span style={{ fontSize: 11.5, fontWeight: 500 }}>{right}</span>}
        </div>
    );
}

function Sources({ cv, ai }: { cv: boolean; ai: boolean }) {
    return (
        <div style={{ display: 'flex', gap: 5 }}>
            <SourceTag icon={FileText} label="CV" present={cv} />
            <SourceTag icon={Mic} label="AI" present={ai} />
        </div>
    );
}

function SourceTag({ icon: Icon, label, present }: { icon: typeof FileText; label: string; present: boolean }) {
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '1.5px 6px', borderRadius: 5,
                fontSize: 9.5, fontWeight: 600,
                background: present ? SURFACE_SECONDARY : 'transparent',
                color: present ? TEXT_SECONDARY : TEXT_QUATERNARY,
                border: `1px solid ${BORDER_MUTED}`,
            }}
            title={`${label} ${present ? 'evaluated' : 'not evaluated'}`}
        >
            <Icon size={9} strokeWidth={2} />
            {label}
            <span style={{ color: present ? FG_SUCCESS : TEXT_QUATERNARY, fontWeight: 700 }}>
                {present ? '✓' : '–'}
            </span>
        </span>
    );
}

function RequirementRow({
    req, justification, isLast,
}: {
    req: AggregatedRequirement;
    justification?: { cv?: string; ai?: string };
    isLast: boolean;
}) {
    const eff = req.effectiveVerdict;
    const chipBg = eff === true ? BG_SUCCESS : eff === false ? BG_DANGER : SURFACE_TERTIARY;
    const chipFg = eff === true ? FG_SUCCESS : eff === false ? FG_DANGER : TEXT_TERTIARY;
    const chipLabel = eff === true ? 'Met' : eff === false ? 'Not met' : 'Pending';

    return (
        <div
            style={{
                padding: '11px 0',
                borderBottom: isLast ? 'none' : `1px solid ${BORDER_MUTED}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, color: TEXT_PRIMARY, lineHeight: 1.4, fontWeight: 500 }}>
                    {req.label}
                </span>
                <span
                    style={{
                        flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center', gap: 5,
                        padding: '3px 9px', borderRadius: 999,
                        background: chipBg, color: chipFg,
                        fontSize: 11.5, fontWeight: 600,
                    }}
                >
                    {eff === true ? <CheckCircle2 size={11} strokeWidth={2.5} /> :
                     eff === false ? <X size={10} strokeWidth={2.75} /> :
                     <Clock size={10} strokeWidth={2} />}
                    {chipLabel}
                </span>
            </div>
            <JustificationBlock justification={justification} indent={0} />
        </div>
    );
}

function BoolBadge({ label, verdict }: { label: string; verdict: boolean | null }) {
    const fg = verdict === true ? FG_SUCCESS : verdict === false ? FG_DANGER : TEXT_QUATERNARY;
    const sym = verdict === true ? '✓' : verdict === false ? '✗' : '–';
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '2px 7px', borderRadius: 5,
                fontSize: 10.5, fontWeight: 600,
                background: SURFACE_TERTIARY,
                color: fg,
                fontVariantNumeric: 'tabular-nums',
            }}
        >
            {label}
            <span style={{ color: fg, fontWeight: 700 }}>{sym}</span>
        </span>
    );
}

function CriterionRow({
    crit, totalWeight, justification, isLast,
}: {
    crit: AggregatedCriterion;
    totalWeight: number;
    justification?: { cv?: string; ai?: string };
    isLast: boolean;
}) {
    const eff = crit.effectiveScore;
    const weightPct = totalWeight === 0 ? 0 : (crit.weight / totalWeight) * 100;

    // Map raw score to a 1..5 bucket regardless of scoreMax, so the chip
    // labels (Not Fit / Weak / OK / Good / Strong Fit) read the same across
    // criteria with different maxes.
    const bucket: 0 | 1 | 2 | 3 | 4 | 5 = (() => {
        if (eff == null || crit.scoreMax <= 0) return 0;
        const pct = eff / crit.scoreMax;
        if (pct <= 0.2)  return 1;
        if (pct <= 0.4)  return 2;
        if (pct <= 0.6)  return 3;
        if (pct <= 0.8)  return 4;
        return 5;
    })();
    // Prefer the user-defined level name when the rubric supplies one,
    // otherwise fall back to the canonical 1→5 chip labels per user spec.
    const FALLBACK_LEVEL_NAMES = ['', 'Not Fit', 'Weak', 'OK', 'Good', 'Strong Fit'];
    const levelName = crit.levels?.find((l) => l.score === bucket)?.name || FALLBACK_LEVEL_NAMES[bucket] || 'Pending';

    const chipBg = bucket >= 4 ? BG_SUCCESS
        : bucket === 3 ? SURFACE_TERTIARY
        : bucket >= 1 ? BG_DANGER
        : SURFACE_TERTIARY;
    const chipFg = bucket >= 4 ? FG_SUCCESS
        : bucket === 3 ? TEXT_SECONDARY
        : bucket >= 1 ? FG_DANGER
        : TEXT_TERTIARY;

    return (
        <div
            style={{
                padding: '11px 0',
                borderBottom: isLast ? 'none' : `1px solid ${BORDER_MUTED}`,
            }}
        >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13.5, fontWeight: 500, color: TEXT_PRIMARY, lineHeight: 1.4 }}>
                        {crit.title || crit.label}
                    </div>
                    <div style={{ marginTop: 3, fontSize: 11, color: TEXT_QUATERNARY }}>
                        weight {Math.round(weightPct)}%
                    </div>
                </div>
                <span
                    style={{
                        flexShrink: 0,
                        display: 'inline-flex', alignItems: 'center',
                        padding: '3px 10px', borderRadius: 999,
                        background: chipBg, color: chipFg,
                        fontSize: 11.5, fontWeight: 600,
                    }}
                >
                    {levelName}
                </span>
            </div>
            <JustificationBlock justification={justification} indent={0} />
        </div>
    );
}

/* ───────── Justification block ───────── */

function JustificationBlock({
    justification, indent,
}: {
    justification?: { cv?: string; ai?: string };
    indent: number;
}) {
    if (!justification || (!justification.cv && !justification.ai)) return null;
    const sources: Array<{ label: 'CV' | 'AI'; text: string }> = [];
    if (justification.cv) sources.push({ label: 'CV', text: justification.cv });
    if (justification.ai) sources.push({ label: 'AI', text: justification.ai });
    const both = sources.length > 1;

    return (
        <div style={{ paddingLeft: indent, marginTop: 6, display: 'flex', flexDirection: 'column', gap: 5 }}>
            {sources.map((s) => (
                <div
                    key={s.label}
                    style={{
                        display: 'flex', gap: 7, alignItems: 'flex-start',
                        fontSize: 12, lineHeight: 1.55,
                        color: TEXT_TERTIARY,
                    }}
                >
                    {both && (
                        <span
                            style={{
                                flexShrink: 0,
                                fontSize: 9.5, fontWeight: 700, letterSpacing: '0.04em',
                                padding: '1.5px 5px', borderRadius: 4,
                                background: SURFACE_TERTIARY,
                                color: TEXT_SECONDARY,
                                marginTop: 1,
                            }}
                        >
                            {s.label}
                        </span>
                    )}
                    <span
                        style={{
                            flex: 1, minWidth: 0,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            fontStyle: 'italic',
                        }}
                        title={s.text}
                    >
                        {s.text}
                    </span>
                </div>
            ))}
        </div>
    );
}

function ScoreBadge({ label, score, scoreMax }: { label: string; score: number | null; scoreMax: number }) {
    const present = score !== null;
    return (
        <span
            style={{
                display: 'inline-flex', alignItems: 'center', gap: 3,
                padding: '1px 7px', borderRadius: 5,
                fontSize: 10.5, fontWeight: 600,
                background: present ? SURFACE_TERTIARY : 'transparent',
                color: present ? TEXT_SECONDARY : TEXT_QUATERNARY,
                border: `1px solid ${BORDER_MUTED}`,
                fontVariantNumeric: 'tabular-nums',
            }}
            title={present ? `${label}: ${score}/${scoreMax}` : `${label}: not evaluated`}
        >
            {label}
            <span style={{ fontWeight: 700 }}>{present ? formatScore(score!) : '–'}</span>
        </span>
    );
}

/* ───────── Idibu sync footer (only on terminal states) ───────── */

function IdibuSyncFooter({ application }: { application: Application }) {
    const [syncing, setSyncing] = useState(false);
    const [syncResult, setSyncResult] = useState<string | null>(null);

    const handleSync = useCallback(async () => {
        setSyncing(true);
        setSyncResult(null);
        try {
            const { data } = await idibuApi.syncApplicantStatus(application._id);
            setSyncResult(data.synced ? `Synced ${application.overallStatus}` : (data.reason || 'Sync skipped'));
        } catch (err: unknown) {
            setSyncResult(err instanceof Error ? err.message : 'Sync failed');
        } finally {
            setSyncing(false);
        }
    }, [application._id, application.overallStatus]);

    return (
        <div style={{
            padding: '8px 22px',
            borderTop: `1px solid ${BORDER_MUTED}`,
            display: 'flex', alignItems: 'center', gap: 8,
            background: SURFACE_SECONDARY,
        }}>
            <button
                onClick={handleSync}
                disabled={syncing}
                style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: '4px 10px', borderRadius: 9999,
                    fontSize: 11.5, fontWeight: 500,
                    background: SURFACE_PRIMARY,
                    border: `1px solid ${BORDER_DEFAULT}`,
                    color: TEXT_SECONDARY, cursor: syncing ? 'wait' : 'pointer',
                    fontFamily: 'inherit',
                }}
            >
                <RefreshCw size={12} strokeWidth={1.75} className={syncing ? 'animate-spin' : ''} />
                {syncing ? 'Syncing…' : `Sync ${application.overallStatus} to Idibu`}
            </button>
            {syncResult && (
                <span style={{ fontSize: 11.5, color: TEXT_TERTIARY }}>{syncResult}</span>
            )}
        </div>
    );
}

/* ───────── RIGHT — editable detail tabs ───────── */

interface RightPanelProps {
    application: Application;
    activeTab: RightTabKey;
    onTabChange: (tab: RightTabKey) => void;
    cvScreening: CvScreeningResult | null;
    aiInterview: AiInterviewResult | null;
    onEdited: (edit: ReviewerScoreEdit) => void;
    userName: string;
    task: TaskItem | null;
    scope: { reportKinds: string[] } | null;
}

function RightPanel({
    application, activeTab, onTabChange,
    cvScreening, aiInterview, onEdited,
    userName, task, scope,
}: RightPanelProps) {
    return (
        <div
            style={{
                flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column',
                background: SURFACE_PRIMARY,
                border: `1px solid ${BORDER_DEFAULT}`,
                borderRadius: 14,
                overflow: 'hidden',
            }}
        >
            <div style={{ flex: 1, minHeight: 0, padding: '14px 16px', display: 'flex', flexDirection: 'column' }}>
                <ReportRightPanel
                    reportKind={cvScreening ? 'cv_screening' : 'ai_interview'}
                    cvScreening={cvScreening}
                    aiInterview={aiInterview}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    applicationId={application._id}
                    currentUserName={userName}
                    jobId={task?.jobId}
                    taskId={task?._id}
                    onEdited={onEdited}
                    scope={scope || undefined}
                />
            </div>
        </div>
    );
}

/* ───────── Action footer ───────── */

interface ActionFooterProps {
    application: Application;
    pipelineStages: Pipeline['stages'];
    task: TaskItem | null;
    markingTask: boolean;
    onMarkComplete: () => void;
    onRefresh: () => void;
}

function ActionFooter({ application, pipelineStages, task, markingTask, onMarkComplete, onRefresh }: ActionFooterProps) {
    return (
        <footer
            style={{
                position: 'relative',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                padding: '12px 22px',
                background: SURFACE_SECONDARY,
                borderTop: `1px solid ${BORDER_MUTED}`,
                flexShrink: 0,
                minHeight: 64,
            }}
        >
            {/* Subtle inner glow line for separation */}
            <div
                aria-hidden
                style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 1,
                    background: 'linear-gradient(90deg, transparent 0%, rgba(0,0,0,0.04) 50%, transparent 100%)',
                    pointerEvents: 'none',
                }}
            />
            <ApplicationActionBar
                application={application}
                pipelineStages={pipelineStages}
                onRefresh={onRefresh}
            />
            {task && task.status !== 'completed' && task.status !== 'revoked' && (
                <>
                    <span style={{ width: 1, height: 22, background: BORDER_MUTED, margin: '0 4px' }} />
                    <button
                        type="button"
                        onClick={onMarkComplete}
                        disabled={markingTask}
                        style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            height: 36, padding: '0 16px',
                            borderRadius: 10,
                            fontSize: 13, fontWeight: 600,
                            background: BG_SUCCESS,
                            color: FG_SUCCESS,
                            border: '1px solid rgba(26,127,55,0.18)',
                            cursor: markingTask ? 'wait' : 'pointer',
                            opacity: markingTask ? 0.6 : 1,
                            fontFamily: 'inherit',
                        }}
                    >
                        <CheckCircle2 size={14} strokeWidth={2.25} />
                        {markingTask ? 'Marking…' : 'Mark complete'}
                    </button>
                </>
            )}
        </footer>
    );
}
